import { Network } from '@capacitor/network';
import { Preferences as Storage } from '@capacitor/preferences';
import { ToastProps } from '@chakra-ui/react';
import { CapacitorWifiConnect } from '@falconeta/capacitor-wifi-connect';
import { getCurrentSession } from 'clipsal-cortex/src/common/api/api-helpers';
import CryptoJS from 'crypto-js';

import { WifiNetwork } from '../../../common/components/WifiNetworkList';
import { ENV_TYPE_SHORTHAND } from '../../../env-type';
import { REGION_SHORTHAND } from '../../../region-type';
import { timeout } from '../../../utils/common/common-utils';
import { FeatureFlag, getFlagValue } from '../../../utils/common/feature-flag-helpers';
import { BASE_IPV4_ADDRESS, EVSE_API_USERNAME, JS_TEST_SERVER_URL } from '../constants';
import { MTLSRequest } from '../mtls-helper';
import { EVSECerts, EVSEToken, WifiData, WifiListNetwork } from '../types';

const HELIOS_BASE_URL = import.meta.env[`VITE_${REGION_SHORTHAND}_${ENV_TYPE_SHORTHAND}_ENDPOINT`] as string;
const EVSE_API_URL = `${BASE_IPV4_ADDRESS}/evse/v1`;
const WIFI_MAX_RETRIES = 3;
const WIFI_RETRY_DELAY_MS = 1000;
const isLoggingEnabled = getFlagValue(FeatureFlag.EnableEVSELogs);

// Perform a mTLS handshake preflight with the test server to verify certs.
export async function mtlsHandshakePreflight(): Promise<EVSECerts> {
  const certs = await fetchEVSECerts();
  await Storage.set({ key: 'evseCerts', value: JSON.stringify(certs) });
  const cert = await fetchAndDecryptEVSECerts();
  const response = await MTLSRequest<EVSECerts>({
    url: `${JS_TEST_SERVER_URL}/test`,
    cert,
  });

  await Storage.remove({ key: 'evseCerts' });
  return response.data;
}

export async function fetchEVSECerts(): Promise<{ ciphertext: string; init_vector: string }> {
  const { token } = await getCurrentSession();
  const response = await fetch(`${HELIOS_BASE_URL}/v1/security/fetch_certs`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch certs');
  }
  return await response.json();
}

/**
 * Authenticates with the EVSE Charger using the provided password and retrieves an authentication token.
 *
 * @param password - The password used to authenticate with the EVSE Charger.
 * @returns A promise that resolves to the authentication token as a string.
 * @throws Error if authentication fails or the token is not returned.
 */
export async function getAuthToken(password: string, toast: (props: ToastProps) => void): Promise<string> {
  isLoggingEnabled &&
    toast({
      title: 'DBG: Decrypting certs',
      status: 'info',
      isClosable: true,
    });
  const cert = await fetchAndDecryptEVSECerts(toast);
  isLoggingEnabled &&
    toast({
      title: 'DBG: Certs decrypted',
      status: 'info',
      isClosable: true,
    });
  isLoggingEnabled &&
    toast({
      title: 'DBG: Sending login request',
      description: `Data: ${JSON.stringify({ username: EVSE_API_USERNAME, password })}`,
      status: 'info',
      isClosable: true,
      duration: 8000,
    });
  let response;
  // Retry logic for Wi-Fi connection errors.
  for (let attempt = 1; attempt <= WIFI_MAX_RETRIES; attempt++) {
    try {
      response = await MTLSRequest<EVSEToken>({
        url: `${EVSE_API_URL}/login/login`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: {
          username: EVSE_API_USERNAME,
          password,
        },
        cert,
      });
      break; // Success, exit retry loop.
    } catch (error) {
      const isWiFiError = (error as { errorMessage?: string })?.errorMessage?.includes('offline');

      if (isWiFiError && attempt < WIFI_MAX_RETRIES) {
        await timeout(WIFI_RETRY_DELAY_MS);
        continue; // Retry.
      }

      // Either not a Wi-Fi error, or we've exhausted retries.
      throw error;
    }
  }
  isLoggingEnabled &&
    toast({
      title: 'DBG: Login request sent',
      description: `Response: ${JSON.stringify(response)}`,
      status: 'info',
      isClosable: true,
      duration: 8000,
    });

  if (!response?.data?.token) throw Error('Failed to login to the EVSE Charger');
  return response.data.token;
}

/**
 * Fetch for nearby Wi-Fi networks (from the POV of the wi-fi device we are connected to).
 * Polling until the networks are ready or the maximum number of attempts is reached.
 *
 * @param authToken - The authorization token used for authorization in the request header.
 * @param maxAttempts - The maximum number of polling attempts before giving up. Defaults to 10.
 * @param pollDelayMs - The delay in milliseconds between polling attempts. Defaults to 1000 ms.
 * @returns An array of Wi-Fi networks.
 */
export async function fetchWifiNetworks(
  authToken: string,
  toast: (props: ToastProps) => void,
  maxAttempts = 20,
  pollDelayMs = 1000
): Promise<WifiNetwork[]> {
  let response;
  let attempts = 0;

  const cert = await fetchAndDecryptEVSECerts(toast);
  do {
    response = await MTLSRequest<WifiListNetwork[] | string>({
      url: `${EVSE_API_URL}/getwifilist`,
      method: 'GET',
      headers: {
        Authorization: authToken,
      },
      cert,
    });

    if (response.status !== 202) break;
    attempts++;
    await timeout(pollDelayMs);
  } while (attempts < maxAttempts);

  if (response.status === 202) {
    throw new Error('Wi-Fi network list not ready after maximum polling attempts');
  }

  if (response.status !== 200) {
    throw new Error(`Failed to fetch Wi-Fi networks. Status: ${response.status}`);
  }

  if (!Array.isArray(response.data)) {
    throw new Error('Invalid Wi-Fi network data format');
  }

  return response.data
    .filter((wifi) => wifi.ssid && wifi.ssid.trim() !== '')
    .map<WifiNetwork>((wifi) => ({ signal: wifi.signalStrength, ssid: wifi.ssid, security: [] }))
    .sort((a, b) => b.signal - a.signal);
}

/**
 * Set a specific Wi-Fi network to the device.
 *
 * @param wifiData - An object containing the WiFi SSID and password.
 * @param authToken - The authorization token used for authorization in the request header.
 * @returns A promise that resolves when the WiFi settings have been successfully updated.
 */
export async function putWifiSettings(
  wifiData: WifiData,
  authToken: string,
  toast: (props: ToastProps) => void
): Promise<void> {
  const cert = await fetchAndDecryptEVSECerts(toast);
  await MTLSRequest({
    url: `${EVSE_API_URL}/wificonnectivitysettings`,
    method: 'PUT',
    headers: {
      Authorization: authToken,
      'Content-Type': 'application/json',
    },
    body: {
      ssid: wifiData.ssid,
      securityOption: true,
      wifiPasscode: wifiData.password,
    },
    cert,
  });
}

/**
 * Decrypts EVSE certificates using AES decryption.
 *
 * @param encrypted - The base64-encoded encrypted certificate data
 * @param iv - The base64-encoded initialization vector used for decryption
 * @param base64Key - The base64-encoded encryption key
 *
 * @returns An object containing the decrypted certificate data:
 * - `client_cert_p12_b64`: Base64-encoded client certificate in PKCS#12 format
 * - `client_key`: The client private key
 * - `server_cert_b64`: Base64-encoded server certificate
 */
export function decryptEVSECerts(
  encrypted: string,
  iv: string,
  base64Key: string,
  toast?: (props: ToastProps) => void
): { client_cert_p12_b64: string; client_key: string; server_cert_b64: string } {
  try {
    const key = CryptoJS.enc.Base64.parse(base64Key);
    const ivWord = CryptoJS.enc.Base64.parse(iv);
    // Decode from base64 text using CipherParams
    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: CryptoJS.enc.Base64.parse(encrypted),
    });
    const decrypted = CryptoJS.AES.decrypt(cipherParams, key, { iv: ivWord });
    const plaintext = decrypted.toString(CryptoJS.enc.Utf8);
    return JSON.parse(plaintext);
  } catch (e) {
    isLoggingEnabled &&
      toast &&
      toast({
        title: 'DSDBG: Error decrypting EVSE certs; Failback certs used',
        description: JSON.stringify(e),
        status: 'info',
        isClosable: true,
        duration: 8000,
      });

    // TODO: Remove the failback values after the root cause of decryption failure is resolved.
    const p12 = import.meta.env.VITE_EVSE_CERTS_CLIENT_P12_B64 as string;
    const clientKey = import.meta.env.VITE_EVSE_CERTS_CLIENT_KEY as string;
    const serverCert = import.meta.env.VITE_EVSE_CERTS_SERVER_B64 as string;
    return { client_cert_p12_b64: p12, client_key: clientKey, server_cert_b64: serverCert };
  }
}

/**
 * Fetches and decrypts EVSE certs in one step for chaining.
 *
 * @returns A Promise that resolves to an {@link EVSECerts} object containing:
 * - `clientP12Base64`: Base64-encoded client certificate in PKCS#12 format
 * - `p12Password`: The client private key
 * - `serverCABase64`: Base64-encoded server certificate
 */
export async function fetchAndDecryptEVSECerts(toast?: (props: ToastProps) => void): Promise<EVSECerts> {
  const evseCerts = await Storage.get({ key: 'evseCerts' });
  if (!evseCerts.value) {
    isLoggingEnabled &&
      toast &&
      toast({
        title: 'DBG: EVSE certs are missing',
        description: `Certs: ${JSON.stringify(evseCerts)}`,
        status: 'info',
        isClosable: true,
        duration: 8000,
      });
    throw new Error('No EVSE certs found in storage');
  }
  const { ciphertext, init_vector: iv } = JSON.parse(evseCerts.value);
  const base64Key = import.meta.env.VITE_EVSE_CERTS_DECRYPTION_KEY as string;
  const decrypted = decryptEVSECerts(ciphertext, iv, base64Key, toast);

  isLoggingEnabled &&
    toast &&
    toast({
      title: 'DBG: EVSE certs decrypted',
      description: `Decrypted length: p12:${JSON.stringify(decrypted.client_cert_p12_b64).length} pass:${
        JSON.stringify(decrypted.client_key).length
      } server:${JSON.stringify(decrypted.server_cert_b64).length}`,
      status: 'info',
      isClosable: true,
      duration: 8000,
    });
  return {
    clientP12Base64: decrypted.client_cert_p12_b64,
    p12Password: decrypted.client_key,
    serverCABase64: decrypted.server_cert_b64,
  };
}

/**
 * Attempts to connect the mobile device to the selected home Wi-Fi network and verify internet connectivity.
 *
 * Android:
 * - Will disconnect from the EVSE AP if needed and reconnect to the home network.
 *
 * iOS:
 * - Disconnecting from the EVSE AP is ineffective.
 * - If iOS reconnects to the EVSE AP, the function throws an error.
 *
 * @param currentSsid - The SSID of the currently connected Wi-Fi network.
 * @param homeSsid - The SSID of the home Wi-Fi network.
 * @param password - Optional password for the home Wi-Fi network.
 * @throws {Error} If connection fails, device remains on EVSE AP, or home Wi-Fi has no internet.
 */
export async function connectToSelectedNetwork(
  currentSsid: string,
  homeSsid: string,
  t: (key: string) => string,
  password?: string
) {
  try {
    const isOnHomeSsid = currentSsid === homeSsid;

    // Force connect only if not already on the desired home network.
    if (!isOnHomeSsid) {
      const attemptConnect = async () => {
        if (password) {
          await CapacitorWifiConnect.secureConnect({
            ssid: homeSsid,
            password,
            saveNetwork: false,
          });
        } else {
          await CapacitorWifiConnect.connect({ ssid: homeSsid });
        }
      };

      try {
        await attemptConnect();
      } catch (e) {
        // Verify current SSID.
        const current = await CapacitorWifiConnect.getDeviceSSID();
        const currentSsid = current?.value;
        if (currentSsid !== homeSsid) {
          await attemptConnect();
        }
      }

      await timeout(3_000);
    }

    const ssidAfterConnect = await CapacitorWifiConnect.getDeviceSSID();
    if (ssidAfterConnect.value !== homeSsid) {
      throw new Error(t('EVSE.device did not remain'));
    }

    try {
      const internetStatus = await Network.getStatus();
      if (!internetStatus.connected) {
        throw new Error(t('EVSE.device not connected'));
      }
    } catch (err) {
      // Handle no internet case
      throw new Error(t('EVSE.no internet'));
    }
  } catch (e) {
    throw new Error(t('EVSE.connect global error'));
  }
}
